import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { updateProfile, updateEmail } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { auth, db, authHelpers } from '../../../firebaseClient';

// Resizes + compresses an image client-side before it's stored as base64,
// keeping it well under Firestore's 1MiB per-document limit regardless of
// how large the original upload was.
const compressImage = (file, maxDim = 400, quality = 0.82) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else if (height > maxDim) {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => reject(new Error('That image could not be read. Try a different file.'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.readAsDataURL(file);
  });
};

// Small self-contained toast so feedback always shows, even if a parent
// component doesn't wire up onSaved.
const Toast = ({ toast }) => {
  if (!toast) return null;
  const isError = toast.type === 'error';
  const isLoading = toast.type === 'loading';
  return (
    <div
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100000] flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg text-sm font-medium text-white transition-all
        ${isError ? 'bg-red-600/95' : isLoading ? 'bg-neutral-800/95' : 'bg-emerald-600/95'}`}
    >
      {isLoading ? (
        <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
      ) : (
        <Icon name={isError ? 'AlertTriangle' : 'CheckCircle2'} size={16} />
      )}
      {toast.message}
    </div>
  );
};

const EditProfileModal = ({ isOpen, onClose, user, onSaved }) => {
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [password, setPassword] = useState('');
  const [newAvatar, setNewAvatar] = useState(null); // compressed base64, or null if unchanged
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessingPhoto, setIsProcessingPhoto] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);
  const toastTimerRef = useRef(null);

  // Reset local state whenever the modal is (re)opened for a fresh user snapshot
  useEffect(() => {
    if (isOpen) {
      setName(user?.name || '');
      setEmail(user?.email || '');
      setNewAvatar(null);
      setPassword('');
      setShowPasswordField(false);
      setError('');
    }
  }, [isOpen, user]);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  const showToast = (message, type = 'success', persist = false) => {
    clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    if (!persist) {
      toastTimerRef.current = setTimeout(() => setToast(null), 3000);
    }
  };

  if (!isOpen) return null;

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file later
    e.target.value = '';
    if (!file) return;

    setError('');

    if (!file.type.startsWith('image/')) {
      const msg = 'That file type isn\u2019t supported. Please choose an image (JPG, PNG, etc).';
      setError(msg);
      showToast(msg, 'error');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      const msg = 'That image is too large. Please choose one under 8MB.';
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    setIsProcessingPhoto(true);
    showToast('Preparing your photo\u2026', 'loading', true);
    try {
      const compressed = await compressImage(file);
      setNewAvatar(compressed);
      showToast('Photo ready \u2014 don\u2019t forget to save', 'success');
    } catch (err) {
      const msg = err?.message || 'Could not process that image. Please try another.';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setIsProcessingPhoto(false);
    }
  };

  const handleClose = () => {
    if (isSaving || isProcessingPhoto) return;
    setPassword('');
    setShowPasswordField(false);
    setError('');
    onClose();
  };

  const handleSave = async () => {
    setError('');
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) {
      setError('Name cannot be empty.');
      return;
    }

    const nameChanged = trimmedName !== (user?.name || '');
    const emailChanged = trimmedEmail !== (user?.email || '');
    const photoChanged = !!newAvatar;

    if (!nameChanged && !emailChanged && !photoChanged) {
      handleClose();
      return;
    }

    if (emailChanged && !password) {
      setShowPasswordField(true);
      setError('Enter your password to confirm the email change.');
      return;
    }

    setIsSaving(true);
    showToast('Saving your changes\u2026', 'loading', true);
    try {
      const currentUser = auth.currentUser;

      if (emailChanged) {
        const { error: reauthError } = await authHelpers.reauthenticate(password);
        if (reauthError) throw new Error('Incorrect password. Please try again.');
        await updateEmail(currentUser, trimmedEmail);
      }

      if (nameChanged) {
        await updateProfile(currentUser, { displayName: trimmedName });
      }

      const firestoreUpdates = { updatedAt: serverTimestamp() };
      if (nameChanged) firestoreUpdates.name = trimmedName;
      if (emailChanged) firestoreUpdates.email = trimmedEmail;
      if (photoChanged) firestoreUpdates.avatar = newAvatar;

      await setDoc(doc(db, 'users', user.id), firestoreUpdates, { merge: true });

      // Push the change up immediately so the rest of the UI updates without
      // a refresh. This fires before the modal closes.
      const updatedProfile = {
        name: nameChanged ? trimmedName : user?.name,
        email: emailChanged ? trimmedEmail : user?.email,
        avatar: photoChanged ? newAvatar : user?.avatar,
      };

      onSaved?.(updatedProfile);
      // Broadcast so anything else mounted on the page — like the nav bar —
      // picks up the change instantly, mirroring the existing
      // "org-profile-updated" pattern used for organization logos.
      window.dispatchEvent(new CustomEvent('user-profile-updated', { detail: updatedProfile }));

      showToast('Profile updated successfully', 'success');
      handleClose();
    } catch (err) {
      console.error('Profile update error:', err);
      const msg =
        err?.code === 'auth/email-already-in-use'
          ? 'That email is already in use by another account.'
          : err?.code === 'auth/requires-recent-login'
          ? 'For security, please re-enter your password to continue.'
          : err?.message || 'Something went wrong. Please try again.';
      setError(msg);
      showToast(msg, 'error');
      if (err?.code === 'auth/requires-recent-login') setShowPasswordField(true);
    } finally {
      setIsSaving(false);
    }
  };

  const busy = isSaving || isProcessingPhoto;

  return createPortal(
    <>
      <Toast toast={toast} />
      <div
        className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      >
        <div
          className="w-full sm:max-w-md max-h-[92vh] sm:max-h-[88vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-white/40 bg-white/95 backdrop-blur-2xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 sm:px-7 pt-5 sm:pt-6 pb-4 border-b border-black/5 flex-shrink-0">
            <h2 className="text-lg font-semibold text-foreground">Edit Profile</h2>
            <button
              onClick={handleClose}
              disabled={busy}
              className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
              aria-label="Close"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5">
            {/* Avatar */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-full overflow-hidden ring-2 ring-white/60 shadow-md bg-muted">
                  <img
                    src={newAvatar || user?.avatar}
                    alt="Avatar preview"
                    className={`w-full h-full object-cover transition-opacity ${isProcessingPhoto ? 'opacity-40' : ''}`}
                  />
                </div>

                {isProcessingPhoto && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="w-6 h-6 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center shadow-md hover:bg-accent/90 transition-colors disabled:opacity-50"
                  aria-label="Change photo"
                >
                  <Icon name="Camera" size={14} />
                </button>
              </div>

              {/* Visually hidden (not display:none) so mobile browsers reliably
                  open the native file picker on click */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                disabled={busy}
                className="absolute w-px h-px p-0 -m-px overflow-hidden opacity-0 pointer-events-none"
                tabIndex={-1}
              />
              <p className="text-xs text-muted-foreground mt-2">
                {isProcessingPhoto ? 'Preparing photo\u2026' : 'Tap the camera icon to change your photo'}
              </p>
            </div>

            {/* Name */}
            <div className="mb-4">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                className="w-full rounded-lg border border-black/10 bg-white/70 px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
              />
            </div>

            {/* Email */}
            <div className="mb-2">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (e.target.value.trim() === user?.email) setShowPasswordField(false);
                }}
                disabled={busy}
                className="w-full rounded-lg border border-black/10 bg-white/70 px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
              />
            </div>

            {showPasswordField && (
              <div className="mb-2 mt-3">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Confirm password to change email
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                  className="w-full rounded-lg border border-black/10 bg-white/70 px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-60"
                  placeholder="Current password"
                />
              </div>
            )}

            {error && <p className="text-xs text-error mt-3">{error}</p>}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 px-5 sm:px-7 py-4 border-t border-black/5 flex flex-col-reverse sm:flex-row gap-2.5">
            <Button variant="ghost" className="w-full sm:flex-1" onClick={handleClose} disabled={busy}>
              Cancel
            </Button>
            <Button variant="default" className="w-full sm:flex-1" onClick={handleSave} disabled={busy}>
              {isSaving ? 'Saving\u2026' : isProcessingPhoto ? 'Processing photo\u2026' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default EditProfileModal;